import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  updateClassroom,
  getClassroomById,
} from "../../../api/pedagogicElement";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  classroomID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

const EditClassroomModal = ({ classroomID, show, onClose, onSaved }: Props) => {
  //Schéma de notre formulaire
  const createSchema = z.object({
    label: z.string().nonempty("Le libellé du niveau d'étude est requis"),
    abbreviation: z.string().nonempty("L'abréviation est requis"),
    statut: z.boolean(),
    capacity: z
      .number({ invalid_type_error: "La capacité de la salle est requise" })
      .min(1, "La capacité doit être au minimum 1"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateClassroom(data, classroomID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Salle de classe mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !classroomID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchClassroom = async () => {
      try {
        const response = await getClassroomById(classroomID);
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Salle de classe introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
          abbreviation: response.data.abbreviation,
          capacity: response.data.capacity.toString(),
          statut: response.data.statut,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        onClose();
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    fetchClassroom();
  }, [show, classroomID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifer une Salle de Classe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Salle A1 ou Salle Labo"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abréviation
                  </label>
                  <input
                    {...register("abbreviation")}
                    type="text"
                    className={
                      errors.abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: A1 ou A2"
                  />
                  {errors.abbreviation && (
                    <div className="invalid-feedback">
                      {errors.abbreviation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Capacité
                  </label>
                  <input
                    {...register("capacity", { valueAsNumber: true })}
                    type="number"
                    className={
                      errors.capacity
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 40"
                  />
                  {errors.capacity && (
                    <div className="invalid-feedback">
                      {errors.capacity.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="form-check form-check-lg form-switch mb-3">
                  <label className="form-check-label" htmlFor="switch-lg">
                    Actif/Inactif
                  </label>
                  <input
                    {...register("statut")}
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switch-lg"
                  />
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditClassroomModal,
  [buildPermission(Resource.CLASSROOM, Action.UPDATE)],
  "masquer",
);
